import type { PlaybackSegment } from '@lucimark/shared/helpers/playbackScheduling'
import type { IChannelPayloadPlaylistAssignment } from '@lucimark/shared/models/player-registration-payload'
import type { IQueueFrame } from '@/types'
import { getPlaybackWorkerPayloadSequenceHash } from '@/helpers/queueHash'

/** Two-slot buffer size in {@link PlaybackScheduler}. */
const FRAME_BUFFER_SIZE = 2

/**
 * Positive modulo — JS `%` is remainder and can be negative (`(-1) % 3 === -1`).
 * Plan-index distance math must stay in `[0, n)`.
 */
export function positiveMod(n: number, mod: number): number {
  if (mod <= 0) {
    return 0
  }
  return ((n % mod) + mod) % mod
}

export function playbackPlanHasBookCps(segments: PlaybackSegment[]): boolean {
  return segments.some(seg => seg.frameId.startsWith('book-'))
}

/**
 * Build the slim worker payload from the main-thread queue (same order as {@link IQueueFrame} playback).
 * Facets/assets stay on the main thread; the worker uses durations + ids for timing and change detection.
 */
export function queueFramesToWorkerPlaybackPlan(
  queue: IQueueFrame[],
  playlists: IChannelPayloadPlaylistAssignment[] | null | undefined
): PlaybackSegment[] {
  const byUrn = new Map((playlists ?? []).map(p => [p.playlistUrn, p]))
  return queue.map((f, queueIndex) => {
    const pl = byUrn.get(f.playlistUrn)
    const frameIndex = pl?.frames.findIndex(x => x.id === f.id) ?? -1
    const resolvedFrameIndex = frameIndex >= 0
      ? frameIndex
      : (f.playlistSeqNo ?? queueIndex)
    return {
      playlistUrn: f.playlistUrn,
      frameIndex: resolvedFrameIndex,
      playlistSeqNo: f.playlistSeqNo,
      durationSeconds: f.duration,
      frameId: f.id,
      playContinuousWithNext: f.playContinuousWithNext === true ? true : undefined
    }
  })
}

/**
 * Resolve a worker slim row to the full {@link IQueueFrame}.
 *
 * The queue is authoritative: expanded embedded-playlist segments use the parent playlist’s `playlistUrn`
 * and `playlistSeqNo` but {@link PlaybackSegment.frameId} is the **embedded** frame id, so
 * `playlists[playlistUrn].frames[frameIndex].id` may not equal `frameId` (parent slot vs expanded child).
 *
 * When `playlistRef` uses random/rotating selection, `rebuildQueue()` can re-run the resolver before the worker
 * receives the new slim plan; worker rows may still carry a previous `frameId` while `playlistSeqNo` is stable.
 * If exactly one queue row matches `(playlistUrn, playlistSeqNo)`, bind to that row (embed `frameCount === 1`).
 * For multi-frame embed expansion, several queue rows share the same seq — keep strict `frameId` match only.
 */
export function findQueueFrameForWorkerSegment(
  queue: IQueueFrame[],
  row: PlaybackSegment,
  _playlists: IChannelPayloadPlaylistAssignment[] | null | undefined
): IQueueFrame | null {
  const strict = queue.find(
    q =>
      q.playlistUrn === row.playlistUrn
      && q.id === row.frameId
      && q.playlistSeqNo === row.playlistSeqNo
  )
  if (strict) {
    return strict
  }
  const seqMatches = queue.filter(
    q => q.playlistUrn === row.playlistUrn && q.playlistSeqNo === row.playlistSeqNo
  )
  if (seqMatches.length === 1) {
    return seqMatches[0]!
  }
  if (seqMatches.length > 1) {
    return seqMatches.find(q => q.id === row.frameId) ?? null
  }
  return null
}

/**
 * Map each worker row to a queue frame in order; skips rows that do not resolve (returns sparse array — prefer filter).
 */
export function resolveQueueFramesFromWorkerPlaybackPlan(
  plan: PlaybackSegment[],
  queue: IQueueFrame[],
  playlists: IChannelPayloadPlaylistAssignment[] | null | undefined
): IQueueFrame[] {
  return plan
    .map(row => findQueueFrameForWorkerSegment(queue, row, playlists))
    .filter((f): f is IQueueFrame => f != null)
}

/** True when worker slim rows match the main-thread plan (ids + order). */
export function workerSegmentsMatchQueuePlan(
  segments: PlaybackSegment[],
  plan: PlaybackSegment[]
): boolean {
  if (segments.length === 0 || plan.length === 0) {
    return true
  }
  return getPlaybackWorkerPayloadSequenceHash(segments) === getPlaybackWorkerPayloadSequenceHash(plan)
}

function segmentRowKey(seg: PlaybackSegment): string {
  return `${seg.frameId}\t${seg.playlistUrn}\t${seg.playlistSeqNo}`
}

function isBookCpsFrameId(frameId: string): boolean {
  return frameId.startsWith('book-')
}

/**
 * Two-slot preload while book CPS is on-air must stay on book rows only.
 * Preloading the next house editorial frame (e.g. HF3 after book-1) decodes video in the hidden slot and bleeds on screen.
 */
export function resolvePresentationPreloadPlanIdx(
  plan: PlaybackSegment[],
  onAirIdx: number
): number | null {
  if (onAirIdx < 0 || onAirIdx >= plan.length || plan.length <= 1) {
    return null
  }
  const onAir = plan[onAirIdx]!
  const nextIdx = (onAirIdx + 1) % plan.length
  if (!isBookCpsFrameId(onAir.frameId)) {
    return nextIdx
  }
  return isBookCpsFrameId(plan[nextIdx]!.frameId) ? nextIdx : null
}

export function resolvePresentationPreloadSegment(
  plan: PlaybackSegment[],
  onAirIdx: number
): PlaybackSegment | null {
  const preloadIdx = resolvePresentationPreloadPlanIdx(plan, onAirIdx)
  return preloadIdx == null ? null : plan[preloadIdx] ?? null
}

/**
 * True when every two-slot worker row exists in the full plan (any index).
 * The buffer is always shorter than a 3+ frame loop; hash equality with the full plan would false-positive as stale.
 */
export function workerBufferSegmentsAreInPlan(
  segments: PlaybackSegment[],
  plan: PlaybackSegment[]
): boolean {
  if (segments.length === 0) {
    return true
  }
  if (plan.length === 0) {
    return false
  }
  const planKeys = new Set(plan.map(segmentRowKey))
  return segments.every(seg => planKeys.has(segmentRowKey(seg)))
}

/**
 * When elapsed/worker state jumps more than one frame ahead (e.g. idx 6 → 1 after queue resync),
 * advance one sequential step instead — preserves HF1 at index 0 on loop wrap.
 */
/**
 * True when elapsed-derived plan index is more than one sequential step ahead of the on-air index
 * (worker must catch up without skipping intermediate frames).
 */
export function isCatchUpBurstAdvance(lastIdx: number, elapsedTargetIdx: number, planLen: number): boolean {
  if (lastIdx < 0 || elapsedTargetIdx < 0 || planLen <= 1) {
    return false
  }
  const last = positiveMod(lastIdx, planLen)
  const target = positiveMod(elapsedTargetIdx, planLen)
  const forwardDistance = positiveMod(target - last, planLen)
  return forwardDistance > 1
}

/**
 * Clamp an elapsed-derived plan index to at most one sequential step ahead of `lastIdx`.
 * Returns indices normalized into `[0, planLen)` (not the raw `targetIdx`) so stale
 * authorities after a queue shrink cannot fail-open past the plan length.
 */
export function clampSequentialPlanIdx(
  lastIdx: number,
  targetIdx: number,
  planLen: number
): number {
  if (lastIdx < 0 || targetIdx < 0 || planLen <= 1) {
    return targetIdx
  }
  /**
   * Stale peak ≥ planLen must map to the last slot (not `positiveMod`), otherwise
   * last→head wrap is rejected and presentation clamps forever (#1662).
   */
  const last = lastIdx >= planLen ? planLen - 1 : positiveMod(lastIdx, planLen)
  const target = positiveMod(targetIdx, planLen)
  const expectedNext = positiveMod(last + 1, planLen)
  const isLegitimateWrap = target === 0 && last === planLen - 1
  const forwardDistance = positiveMod(target - last, planLen)
  if (forwardDistance > 1 && !isLegitimateWrap) {
    return expectedNext
  }
  return target
}

/** Legitimate portal-book loop wrap: last plan slot (e.g. book-1) → HF1 at index 0. */
export function isLegitimatePortalBookLoopWrap(
  prevIdx: number,
  notifiedIdx: number,
  planLen: number
): boolean {
  return notifiedIdx === 0 && prevIdx === planLen - 1 && planLen > 1
}

/**
 * Cap a stale monotonic peak into the current plan before wrap legitimacy checks.
 * `positiveMod` alone is wrong here: peak 16 on planLen 6 → 4, not the last slot (#1662).
 */
export function authorityIdxForPlanLen(authorityIdx: number, planLen: number): number {
  if (planLen <= 0 || authorityIdx < 0) {
    return authorityIdx
  }
  if (authorityIdx < planLen) {
    return authorityIdx
  }
  return planLen - 1
}

/** Mid-loop notify/bootstrap to index 0 while on-air authority is not the last plan slot. */
export function isSpuriousMidLoopHeadReset(
  authorityIdx: number,
  notifiedIdx: number,
  planLen: number
): boolean {
  const auth = authorityIdxForPlanLen(authorityIdx, planLen)
  if (notifiedIdx !== 0 || auth < 1 || planLen <= 1) {
    return false
  }
  return !isLegitimatePortalBookLoopWrap(auth, notifiedIdx, planLen)
}

/**
 * Regressive rewind to loop head while the visible surface (or logged authority) is already past index 0.
 * Covers cold-start races where session authority is still 0 but frame 1+ is already on screen.
 */
export function isRegressivePresentationToLoopHead(args: {
  notifiedIdx: number
  planLen: number
  visiblePlanIdx: number
  loggedPlanIdx: number
}): boolean {
  const { notifiedIdx, planLen, visiblePlanIdx, loggedPlanIdx } = args
  if (notifiedIdx !== 0 || planLen <= 1) {
    return false
  }
  const authorityIdx = Math.max(visiblePlanIdx, loggedPlanIdx)
  if (authorityIdx >= planLen - 1) {
    return false
  }
  return authorityIdx > 0
}

/** Mid-loop scheduler bootstrap to HF1 while authority is still in book block or HF3 visit. */
export function isSpuriousPortalBookMidLoopHouseReset(
  authorityIdx: number,
  notifiedIdx: number,
  planLen: number
): boolean {
  const auth = authorityIdxForPlanLen(authorityIdx, planLen)
  if (notifiedIdx !== 0 || auth < 2 || planLen <= 1) {
    return false
  }
  return !isLegitimatePortalBookLoopWrap(auth, notifiedIdx, planLen)
}

/**
 * Portal book: when rebuild/tick would present HF1 (idx 0) mid-loop, resume at the last
 * stable/synced/snapshot index instead of resetting to loop head.
 */
/**
 * Portal book: worker tick must not skip book CPS slots (e.g. 4→6→0). One sequential step per
 * advance unless legitimate wrap. Returns indices normalized into `[0, planLen)`.
 */
export function clampPortalBookSequentialAdvanceIdx(
  lastNotifiedIdx: number,
  targetIdx: number,
  planLen: number
): number {
  if (lastNotifiedIdx < 0 || targetIdx < 0 || planLen <= 1) {
    return targetIdx
  }
  const last = positiveMod(lastNotifiedIdx, planLen)
  const target = positiveMod(targetIdx, planLen)
  if (target === last) {
    return target
  }
  if (isLegitimatePortalBookLoopWrap(last, target, planLen)) {
    return target
  }
  const expectedNext = positiveMod(last + 1, planLen)
  const forwardDist = positiveMod(target - last, planLen)
  if (forwardDist > 1) {
    return expectedNext
  }
  return target
}

export function resolvePortalBookBootstrapFrameIdx(
  requestedIdx: number,
  opts: {
    prevStableIdx: number
    lastSyncedIdx: number
    resumeSnapshotIdx?: number | null
    planLen: number
  }
): number {
  const { prevStableIdx, lastSyncedIdx, resumeSnapshotIdx = null, planLen } = opts
  if (planLen <= 1) {
    return Math.min(Math.max(0, requestedIdx), planLen - 1)
  }
  let idx = Math.min(Math.max(0, requestedIdx), planLen - 1)
  /** Main-thread {@link lastSyncedResumePlanIdx} is authoritative; stale {@link prevStableIdx} must not legitimize mid-loop wrap. */
  const legitimateWrap = idx === 0 && (
    lastSyncedIdx === planLen - 1
    || (resumeSnapshotIdx != null && resumeSnapshotIdx === planLen - 1)
  )
  let midLoopResume = Math.max(resumeSnapshotIdx ?? -1, lastSyncedIdx, prevStableIdx)
  if (prevStableIdx > lastSyncedIdx && lastSyncedIdx >= 0) {
    midLoopResume = Math.max(resumeSnapshotIdx ?? -1, lastSyncedIdx)
  }
  if (idx === 0 && !legitimateWrap && midLoopResume > 0 && midLoopResume < planLen) {
    idx = midLoopResume
  }
  return idx
}

/** Portal book: reject regressive resume (e.g. 3→0) except legitimate loop wrap (last→0). */
export function shouldAcceptPortalBookResumePlanIdx(
  prev: number,
  planIdx: number,
  planLen: number
): boolean {
  if (prev < 0 || planIdx < 0 || planLen <= 1) {
    return true
  }
  if (isLegitimatePortalBookLoopWrap(prev, planIdx, planLen)) {
    return true
  }
  if (planIdx < prev) {
    return false
  }
  if (planIdx > prev + 1) {
    return false
  }
  return true
}

export function workerBufferMatchesNotifiedIdx(
  segments: PlaybackSegment[],
  plan: PlaybackSegment[],
  notifiedIdx: number
): boolean {
  if (notifiedIdx < 0 || notifiedIdx >= plan.length) {
    return false
  }
  const onAirKey = segmentRowKey(plan[notifiedIdx]!)
  return segments.some(seg => segmentRowKey(seg) === onAirKey)
}

/**
 * After a full loop, {@link refreshEmbeddedPicksAfterLoopWrap} clears random embed memoization and rebuilds so
 * rotating/random house picks can advance before the worker
 * receives the new slim plan. Stale worker segments + flipped pointer then resolve to the wrong slot/frame.
 * When any buffer row is absent from the plan, or the on-air plan row is missing from the buffer while
 * {@link notifiedIdx} is known, rebind the two-slot buffer from the on-air plan row (and its preload successor).
 * Without {@link notifiedIdx}, fall back to plan[0..1] (loop-wrap bootstrap).
 */
/** Rebind two-slot presentation when book CPS is on-air so the hidden slot never preloads house editorial. */
export function applyPortalBookPresentationPreloadPolicy(
  segments: PlaybackSegment[],
  plan: PlaybackSegment[],
  notifiedIdx?: number
): PlaybackSegment[] {
  if (
    notifiedIdx == null
    || notifiedIdx < 0
    || notifiedIdx >= plan.length
    || segments.length === 0
  ) {
    return segments
  }
  const onAir = plan[notifiedIdx]!
  if (!isBookCpsFrameId(onAir.frameId)) {
    return segments
  }
  const preload = resolvePresentationPreloadSegment(plan, notifiedIdx)
  const onAirKey = segmentRowKey(onAir)
  const onAirInBuffer = segments.find(seg => segmentRowKey(seg) === onAirKey) ?? onAir
  if (preload == null) {
    return [onAirInBuffer]
  }
  return [onAirInBuffer, preload]
}

export function coercePresentationToQueuePlan(
  segments: PlaybackSegment[],
  pointer: number,
  plan: PlaybackSegment[],
  notifiedIdx?: number
): { segments: PlaybackSegment[], pointer: number } {
  if (plan.length === 0) {
    return { segments, pointer }
  }
  const inPlan = workerBufferSegmentsAreInPlan(segments, plan)
  const matchesOnAir = notifiedIdx == null
    ? true
    : workerBufferMatchesNotifiedIdx(segments, plan, notifiedIdx)
  let coercedSegments = segments
  let coercedPointer = pointer
  if (!(inPlan && matchesOnAir)) {
    const bufferLen = Math.min(FRAME_BUFFER_SIZE, plan.length)
    if (notifiedIdx != null && notifiedIdx >= 0 && notifiedIdx < plan.length) {
      const onAir = plan[notifiedIdx]!
      const preload = resolvePresentationPreloadSegment(plan, notifiedIdx)
      coercedSegments = preload != null && bufferLen >= 2 ? [onAir, preload] : [onAir]
      coercedPointer = 0
    }
    else {
      coercedSegments = plan.slice(0, bufferLen)
      coercedPointer = 0
    }
  }
  const sanitized = applyPortalBookPresentationPreloadPolicy(coercedSegments, plan, notifiedIdx)
  if (sanitized.length !== coercedSegments.length) {
    coercedPointer = 0
  }
  return { segments: sanitized, pointer: coercedPointer }
}

/** Ensure the visible buffer slot matches {@link queue}[notifiedIdx] when the two-slot buffer is authoritative. */
export function bindPresentationViewToQueueNotifiedIdx(
  frames: IQueueFrame[],
  viewIdx: number,
  notifiedIdx: number,
  queue: IQueueFrame[]
): { frames: IQueueFrame[], viewIdx: number } {
  if (notifiedIdx < 0 || notifiedIdx >= queue.length || frames.length === 0) {
    return { frames, viewIdx }
  }
  const onAir = queue[notifiedIdx]!
  const inBuffer = frames.findIndex(
    f =>
      f.id === onAir.id
      && f.playlistUrn === onAir.playlistUrn
      && (f.playlistSeqNo ?? 0) === (onAir.playlistSeqNo ?? 0)
  )
  if (inBuffer >= 0) {
    return { frames, viewIdx: inBuffer }
  }
  const plan = queue.map((f, queueIndex) => ({
    playlistUrn: f.playlistUrn,
    frameIndex: queueIndex,
    playlistSeqNo: f.playlistSeqNo ?? 0,
    durationSeconds: f.duration,
    frameId: f.id
  }))
  const preloadIdx = resolvePresentationPreloadPlanIdx(plan, notifiedIdx)
  if (preloadIdx == null) {
    return { frames: [onAir], viewIdx: 0 }
  }
  const preload = queue[preloadIdx]!
  return { frames: [onAir, preload], viewIdx: 0 }
}
